import { useContext, useEffect, useState } from 'react';

import { Breadcrumb, Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router';
import OrganizationHeader from '@components/OrganizationHeader/OrganizationHeader';

import LoadingIndicator from '@components/LoadingIndicator/LoadingIndicator';
import { BRAND_NAME } from '@constants/constants';
import { IClinic } from '@customTypes/clinician';
import useUsers from '@hooks/useUsers';
import { ROUTE_PATHS } from '@router/paths';
import { Context } from '@store/store';

import ClinicalTeam from './components/ClinicalTeam/ClinicalTeam';
import OrgDefaultDelegates from './components/OrgDefaultDelegates/OrgDefaultDelegates';

import './MyDelegates.scss';

export default function MyDelegates() {
  const navigate = useNavigate();
  const { fetchUsers, isLoading } = useUsers();
  const [state] = useContext(Context);

  const orderingProviderClinics = state.orderingProviderClinics;
  const [selectedOrganization, setSelectedOrganization] = useState<IClinic | null>(null);

  useEffect(() => {
    if (orderingProviderClinics.length > 0 && !selectedOrganization) {
      setSelectedOrganization(orderingProviderClinics[0]);
    }
  }, [orderingProviderClinics, selectedOrganization]);

  useEffect(() => {
    if (selectedOrganization?.clinic_id) {
      fetchUsers(selectedOrganization.clinic_id, true);
    }
  }, [fetchUsers, selectedOrganization]);

  const goBack = () => {
    navigate(-1);
  };

  if (!orderingProviderClinics.length) {
    navigate(ROUTE_PATHS.ROOT);
    return null;
  }

  return (
    <Stack gap={4} className='layout-single-col-py my-delegates-container'>
      <Breadcrumb listProps={{ className: 'mb-0' }}>
        <Breadcrumb.Item onClick={goBack}>Return</Breadcrumb.Item>
        <Breadcrumb.Item active>Settings</Breadcrumb.Item>
        <Breadcrumb.Item active>My Delegates</Breadcrumb.Item>
      </Breadcrumb>

      <Stack as='section' className='page-title-section'>
        <h1 className='mb-0 page-title'>My Delegates</h1>
        <div className='text-secondary page-subtitle'>
          <p className='mb-1'>
            {BRAND_NAME} aims to simplify the complex, so we&apos;ve made managing access to orders flexible for your
            clinic&apos;s workflow.
          </p>
          <ul className='mb-0'>
            <li>
              <strong>Clinical Teams:</strong> Individuals assigned by a provider to manage and view orders where the
              provider is the ordering provider.
            </li>
            <li>
              <strong>Organization Default Delegates:</strong> Individuals assigned by an organization administrator to
              manage and view all patient orders across the organization.
            </li>
          </ul>
        </div>
      </Stack>

      <section>
        <OrganizationHeader
          clinics={orderingProviderClinics}
          selectedOrganization={selectedOrganization}
          onSelect={setSelectedOrganization}
        />
        <div className='section-wrapper'>
          {isLoading ? (
            <LoadingIndicator />
          ) : (
            <>
              {selectedOrganization && <ClinicalTeam clinicId={selectedOrganization.clinic_id} />}
              <OrgDefaultDelegates />
            </>
          )}
        </div>
      </section>
    </Stack>
  );
}
